import React from 'react';
import {
    Col, Collapse, FormGroup, Progress,
    Row,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { isEqual, debounce, isEmpty } from 'lodash-es';
import AsyncSelect from 'react-select/async';
import moment from 'moment';
import {
    faFileInvoiceDollar,
    faCheckCircle,
    faExclamationTriangle,
    faTrashAlt,
} from '@fortawesome/free-solid-svg-icons';
import { FormPrompt } from '@s20/components/common/FormPrompt';
import { clone } from '@s20/common/util/object';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, { ApiRoutes, AppNavPaths, ServiceResponseResult } from '../Common';
import {
    AppPageForm,
    FlexCenterRow,
    FormCheckbox,
    FormGroupColumn,
    GroupedRow,
    onFieldChange,
    onReactSelectChanged,
    SubHeading,
    ToastMessage,
    Input,
    NumericInput,
    toasty,
    FormLabel,
} from '../common/forms/FormElements';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { handleFormSaveError } from '../common/forms/ValidationError';
import { TipAlert } from '../common/TipAlert';
import { Block } from '../common/Block';
import CreateHolidayForm from '../holiday/CreateHolidayForm';
import { SaveButton } from '../common/forms/SaveButton';
import { ConfirmDelete } from '../common/Modal';
import TimePicker from '../common/forms/TimePicker';
import { YesNoButtons } from '../common/forms/YesNoButtons';
import { USCountyGroupPayRateGrid } from './USCountyGroupPayRateGrid';
import { USCountyGroupStatePayRateGrid } from './USCountyGroupStatePayRateGrid';
import { USCountyGroupBumpRuleGrid } from './USCountyGroupBumpRuleGrid';

const initializePayRates = (workTypes = [], existingRates = []) =>
    [...existingRates, ...workTypes
        .filter((workType) => !existingRates.some(
            (rate) => rate.contractUnionWorkTypeId === workType.id,
        ))
        .map((workType) => ({ contractUnionWorkTypeId: workType.id, fringeRate: 0, payRate: 0 })),
    ];

class USCountyGroup {
    id = null;
    description = '';
    groupType = null;
    usStateId = null;
    usCountyGroupTypeId = null;
    counties = [];
    contractUnionWorkTypes = [];
    workTypePayRates = [];
    usCountyGroupPaySourceId = 1;
    contractScheduleTypeId = 1;
    hasUnionPayrollSchedule = false;
    alwaysHasSaturdayOvertime = false;
    hasCustomBumpRules = false;
    hasCustomShopRate = false;
    bumpRules = [];
    hasCustomHolidayList = false;
    breakOutFringeByCounty = false;
    statePayRates = [];
}
const ContractUnionWorkTypes = {
    PrevailingWage: 5,
};
const getDate = (dateInput, targetFormat = 'HH:mm') => {
    const formats = ['YYYY-MM-DD HH:mm A', 'L-d-YYYY HH:mm A', 'M/D/YYYY HH:mm A'];
    let startDate = '';

    formats.forEach((format) => {
        const date = moment(dateInput, format);

        if (date.isValid()) {
            startDate = date.format(targetFormat);
        }
    });

    return startDate;
};

class USCountyGroupForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        this.formRef = React.createRef();

        this.state = {
            originalData: new USCountyGroup(),
            countyGroup: new USCountyGroup(),
            usStates: [],
            groupTypes: [],
            groupPaySources: [],
            counties: [],
            contractUnionWorkTypes: [],
            contractScheduleTypes: [],
            usCountyGroups: [],
            loading: true,
            saving: false,
            holidays: [],
            selectedHolidays: [],
            defineCustomHolidays: false,
            userGroups: [],
        };

        this.handlers.change = this.handlers.change.bind(this);
        this.handlers.select = this.handlers.select.bind(this);
        this.onDeleteGroupConfirmed = this.onDeleteGroupConfirmed.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps
            && this.props.match.params.id !== (prevProps.match.params ?? {}).id
        ) {
            this.populateState();
        }
    }

    onClearErrors = () => this.setState({ errors: {} });

    getCountyGroupId = () => this.props.match.params.id;

    onTimeChanged = (ev) => {
        const { countyGroup } = this.state;

        const { value, name } = ev.target;

        const timeValue = value
            ? moment(value, ['h:mm A'])
            : '';

        countyGroup[name] = timeValue;
        this.setState({ countyGroup });
    };

    onAddHoliday = () => {
        this.context.setFormOpened(true);
        this.setState({ showAddHolidayForm: true });
    };

    onCloseHolidayForm = () => {
        this.context.setFormOpened(false);
        this.setState({ showAddHolidayForm: false });
    };

    refreshHolidays = async () => {
        const updatedHolidays = await httpClient.js(ApiRoutes.holiday.all());
        this.setState({ holidays: updatedHolidays });
    };

    onHolidayCreated = (newHolidayOption) => {
        this.setState((prevState) => {
            const updatedHolidays = [...prevState.holidays, newHolidayOption];
            const updatedSelected = [...prevState.selectedHolidays, newHolidayOption];

            return {
                holidays: updatedHolidays,
                selectedHolidays: updatedSelected,
            };
        });
    };

    async saveHolidays(countyGroupId) {
        const holidayIds = this.state.selectedHolidays.map((h) => h.value);

        try {
            if (!isEmpty(holidayIds)) {
                await httpClient.post(
                    ApiRoutes.holiday.countyGroupHolidays(countyGroupId),
                    holidayIds,
                );
            }
        } catch (error) {
            console.error(error);
            toast.error('Error defining County Group holidays');
        }
    }

    onSubmit = async () => {
        this.onClearErrors();
        this.setSaving(true);

        const cloned = clone(this.state.countyGroup);

        // Ensure `workTypePayRates` includes `contractUnionWorkTypeId`
        cloned.workTypePayRates = (this.state.countyGroup.workTypePayRates || []).map((rate) => ({
            id: rate.id || 0,
            contractUnionWorkTypeId: rate.contractUnionWorkTypeId,
            payRate: rate.payRate,
            fringeRate: rate.fringeRate,
            usCountyGroupId: this.state.countyGroup.id || 0,
            alwaysHasSaturdayOvertime: rate.alwaysHasSaturdayOvertime ?? false,
            userGroupId: rate.userGroupId,
        }));

        cloned.bumpRules = (this.state.countyGroup.bumpRules || []).map((rule) => ({
            ...rule,
            id: typeof rule.id === 'string' ? 0 : rule.id,
        }));

        if (this.props.location.pathname == AppNavPaths.USCountyGroupNew) {
            await this.create(cloned);
        } else {
            await this.update(cloned);
        }
    };

    setSaving = (b) => this.setState({ saving: b });

    create = async (countyGroup) => {
        countyGroup.id = 0;

        const response = await httpClient
            .post(ApiRoutes.USCounties.group(), countyGroup)
            .catch(this.handleSaveError);

        if (response) {
            await this.saveHolidays(response.data);

            if (response.result === ServiceResponseResult.Ok) {
                toast.success(
                    <ToastMessage
                        icon={faCheckCircle}
                        header="Save Successful"
                        message="Saved."
                    />,
                );

                this.context.history.push(
                    `${AppNavPaths.USCountyGroup}/${response.data}`,
                );
            } else {
                toast.error(
                    <ToastMessage
                        icon={faExclamationTriangle}
                        header="There was a problem saving"
                        message={`${response.message}`}
                    />,
                );
            }
        }
        this.setSaving(false);
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    handlers = {
        change: onFieldChange,
        select: onReactSelectChanged,
    };

    async populateState() {
        const { id } = this.props.match.params;

        const [groupTypes, usStates, contractUnionWorkTypes, countyGroup, contractScheduleTypes, usCountyGroups, groupPaySources, userGroups] = await Promise.all([
            httpClient.js(ApiRoutes.USCounties.groupTypes()),
            httpClient.js(ApiRoutes.typeAheads.USStatesbyCountryId(this.context.tenant.countryId)),
            httpClient.js(ApiRoutes.typeAheads.contractUnionWorkTypes()),
            id ? httpClient.js(ApiRoutes.USCounties.byGroupId(id)) : new USCountyGroup(),
            httpClient
                .js(ApiRoutes.typeAheads.contractScheduleTypes())
                .catch(() => console.log('Unable to get contract schedule types.')),
            httpClient
                .js(ApiRoutes.USCounties.groups())
                .catch(() => console.log('Unable to get county groups')),
            httpClient.js(ApiRoutes.USCounties.groupPaySources()),
            httpClient.js(ApiRoutes.auth.groups()),
        ]);

        const allHolidaysResp = await httpClient.js(ApiRoutes.holiday.all());
        const allHolidays = allHolidaysResp;

        let currentHolidayIds = [];
        if (id) {
            currentHolidayIds = await httpClient.js(ApiRoutes.holiday.countyGroupHolidays(id));
        }

        const selectedHolidays = allHolidays.filter((opt) =>
            currentHolidayIds.includes(opt.value),
        );

        const hasCustom = countyGroup.hasCustomHolidayList || selectedHolidays.length > 0;

        countyGroup.hasCustomHolidayList = hasCustom;

        const normalizedWorkTypes = contractUnionWorkTypes
            .filter((x) => x.isUnionWork)
            .map((workType) => ({
                id: workType.id || workType.value,
                description: workType.description || workType.label,
                isUnionWork: workType.isUnionWork,
            }));

        const isUnion = !!this.context.tenant?.tenantSettings?.enforceContractUnionWork;

        if (!isUnion) {
            const prevailingWageType = normalizedWorkTypes.find((wt) => wt.id === ContractUnionWorkTypes.PrevailingWage);
            countyGroup.contractUnionWorkTypes = prevailingWageType ? [prevailingWageType] : [];
            countyGroup.usCountyGroupTypeId = groupTypes[0]?.id ?? null;
        }

        countyGroup.workTypePayRates = initializePayRates(
            countyGroup.contractUnionWorkTypes,
            countyGroup.workTypePayRates,
        );

        this.setState({
            originalData: clone(countyGroup),
            countyGroup,
            groupPaySources,
            usStates: usStates.map((x) => ({ label: x.label, value: x.id })),
            groupTypes: groupTypes.map((x) => ({ label: x.description, value: x.id })),
            contractUnionWorkTypes: normalizedWorkTypes,
            contractScheduleTypes,
            usCountyGroups: usCountyGroups.filter((group) => group.id !== parseFloat(id) && group.usCountyGroupTypeId === 2),
            loading: false,
            saving: false,
            holidays: allHolidays,
            selectedHolidays,
            defineCustomHolidays: hasCustom,
            userGroups,
        });
    }

    onDefineCustomHolidaysChanged = (e) => {
        const { checked } = e.target;

        this.setState((prev) => ({
            defineCustomHolidays: checked,
            selectedHolidays: checked ? prev.selectedHolidays : [],

            countyGroup: {
                ...prev.countyGroup,
                hasCustomHolidayList: checked,
            },
        }));
    };


    update = async (countyGroup) => {
        const response = await httpClient
            .put(ApiRoutes.USCounties.byGroupId(countyGroup.id), countyGroup)
            .catch(this.handleSaveError);

        if (response) {
            if (response.result === ServiceResponseResult.Ok) {
                await this.saveHolidays(response.data);

                this.setState((prevState) => ({
                    saving: false,
                    formValidated: false,
                    originalData: clone(prevState.countyGroup),
                }));

                toast.success(
                    <ToastMessage
                        icon={faCheckCircle}
                        header="Save Successful"
                        message="County Group saved."
                    />,
                );
            } else {
                toast.error(
                    <ToastMessage
                        icon={faExclamationTriangle}
                        header="There was a problem saving"
                        message={`${response.message}`}
                    />,
                );
            }
        }
        this.setSaving(false);
    };

    onCountyChange = (value) => {
        const { countyGroup } = { ...this.state };

        countyGroup.counties = value;

        this.setState({ countyGroup });
    };

    handleWorkTypeChange = (selectedWorkTypes) => {
        this.setState((prevState) => ({
            countyGroup: {
                ...prevState.countyGroup,
                contractUnionWorkTypes: selectedWorkTypes,
                workTypePayRates: initializePayRates(
                    selectedWorkTypes,
                    prevState.countyGroup.workTypePayRates,
                ),
            },
        }));
    };

    handlePayRateChange = (payRate, value, fieldName = 'payRate') => {
        const { countyGroup } = this.state;

        this.setState({
            countyGroup: {
                ...countyGroup,
                workTypePayRates: countyGroup.workTypePayRates.map((rate) =>
                    rate.contractUnionWorkTypeId === payRate.contractUnionWorkTypeId && rate.userGroupId == payRate.userGroupId
                        ? { ...rate, [fieldName]: parseFloat(value) || 0 }
                        : rate,
                ),
            },
        });
    };

    handleSaturdayOTChange = (payRate, value) => {
        const { countyGroup } = this.state;

        this.setState({
            countyGroup: {
                ...countyGroup,
                workTypePayRates: countyGroup.workTypePayRates.map((rate) =>
                    rate.contractUnionWorkTypeId === payRate.contractUnionWorkTypeId && rate.userGroupId == payRate.userGroupId
                        ? { ...rate, alwaysHasSaturdayOvertime: !!value }
                        : rate,
                ),
            },
        });
    };

    handleAddWorkTypePayRate = (newPayRate) => {
        const { countyGroup } = this.state;
        countyGroup.workTypePayRates = [...countyGroup.workTypePayRates, newPayRate];

        this.setState({
            countyGroup,
        });
    };

    handleRemoveWorkTypePayRate = (payRate) => {
        const { countyGroup } = this.state;
        const updatedWorkTypePayRates = countyGroup.workTypePayRates
            ?.filter((p) => !(p.contractUnionWorkTypeId == payRate.contractUnionWorkTypeId && p.userGroupId == payRate.userGroupId));

        this.setState({
            countyGroup: {
                ...countyGroup,
                workTypePayRates: updatedWorkTypePayRates,
            },
        });
    };

    loadOptions = debounce((searchString, callback) => {
        httpClient.post(ApiRoutes.USCounties.all(), { searchString })
            .then((data) => callback(data));
    }, 250);

    getActions = () => {
        const countyGroupId = this.props.match?.params?.id;

        const actions = [];

        if (countyGroupId) {
            actions.push({
                key: 'delete',
                onClick: () => this.showDeleteModal(),
                icon: faTrashAlt,
                label: 'Delete County Group',
                className: 'text-danger',
                CanProps: {
                    I: 'delete',
                    a: 'county_group',
                },
            });
        }

        if (actions.length > 0) {
            return actions;
        }

        return null;
    };

    onDeleteGroupConfirmed = async () => {
        const { id } = { ...this.props.match.params };

        const result = await httpClient
            .delete(ApiRoutes.USCounties.byGroupId(id))
            .catch(this.handleSaveError);

        if (result) {
            toasty.success('County group deleted.');
            this.context.history.push(`${AppNavPaths.USCountyGroups}`);
        }
    };

    showDeleteModal = () => {
        ConfirmDelete.show({
            title: 'Delete County Group',
            onSubmit: () => this.onDeleteGroupConfirmed(),
            body: () => 'Are you sure you want to delete this county group?',
        });
    };

    renderForm() {
        const {
            groupTypes,
            groupPaySources,
            countyGroup,
            originalData,
            saving,
            errors,
            loading,
            formValidated,
            usStates,
            contractUnionWorkTypes,
            showAddHolidayForm,
            contractScheduleTypes,
            usCountyGroups,
            userGroups,
        } = this.state;

        if (!countyGroup) {
            return '';
        }

        const newCountyGroup = parseInt(countyGroup.id ?? 0) <= 0;
        const tenantSettings = ((this.context ?? {}).tenant ?? {}).tenantSettings ?? {};
        const regularShiftDifferentialEnabled = !!tenantSettings.enableRegularShiftDifferential;

        const customShiftDifferentialStart = countyGroup.customShiftDifferentialStart
            ? getDate(countyGroup.customShiftDifferentialStart)
            : '';

        const customShiftDifferentialEnd = countyGroup.customShiftDifferentialEnd
            ? getDate(countyGroup.customShiftDifferentialEnd)
            : '';

        const isUnion = !!this.context.tenant?.tenantSettings?.enforceContractUnionWork;
        return (
            <>
                <FormPrompt
                    when={!saving && !isEqual(originalData, countyGroup)}
                    message="You have unsaved changes, are you sure you want to leave?"
                />

                <AppPageForm
                    backUrl={AppNavPaths.USCountyGroups}
                    backLabel="Back to County Groups"
                    formShown={this.context.formIsOpen}
                    formId="countyGroupForm"
                    formHeadingIcon={faFileInvoiceDollar}
                    formHeading={
                        newCountyGroup
                            ? 'New County Group'
                            : 'Edit County Group'
                    }
                    actions={this.getActions()}
                    formName="countyGroupForm"
                    formRef={this.formRef}
                    onSubmit={this.onSubmit}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    isValidated={formValidated}
                    saving={saving}
                    errors={errors}
                    loading={loading}
                >
                    <GroupedRow>
                        <FormGroupColumn>
                            <Input
                                label="County Group Name"
                                id="description"
                                name="countyGroup.description"
                                value={countyGroup.description ?? ''}
                                onChange={this.handlers.change}
                                placeholder="Enter Name"
                                required
                                error="Name is required"
                            />
                            {isUnion && (
                                <ValidatedSelect
                                    id="usCountyGroupTypeId"
                                    name="countyGroup.usCountyGroupTypeId"
                                    label="Group Type"
                                    required
                                    options={groupTypes}
                                    value={
                                        (groupTypes ?? []).find(
                                            (s) => s.value
                                                === countyGroup.usCountyGroupTypeId,
                                        ) ?? ''
                                    }
                                    onChange={this.handlers.select.bind(this)}
                                    error="A Group Type selection is required."
                                />
                            )}

                            <Collapse isOpen={countyGroup.usCountyGroupTypeId === 2}>
                                <Input
                                    label="Local number"
                                    id="localNumber"
                                    name="countyGroup.localNumber"
                                    value={countyGroup.localNumber ?? ''}
                                    onChange={this.handlers.change}
                                    required={countyGroup.usCountyGroupTypeId === 2}
                                    error="A local number is required."
                                />
                            </Collapse>
                            {isUnion && (
                                <ValidatedSelect
                                    id="contractUnionWorkTypes"
                                    name="contractUnionWorkTypes"
                                    label="Applicable Union Work Type(s)"
                                    isMulti={isUnion}
                                    required
                                    options={contractUnionWorkTypes}
                                    value={
                                        isUnion
                                            ? (countyGroup.contractUnionWorkTypes ?? [])
                                            : (countyGroup.contractUnionWorkTypes?.[0] ?? '')
                                    }
                                    getOptionLabel={(option) => option.description}
                                    getOptionValue={(option) => option.id}
                                    onChange={isUnion ? this.handleWorkTypeChange : () => { }}
                                    error="At least one work type is required."
                                />
                            )}
                        </FormGroupColumn>

                        <FormGroupColumn>
                            <ValidatedSelect
                                id="usStateId"
                                label="State"
                                name="countyGroup.usStateId"
                                required
                                options={usStates}
                                value={usStates.find((s) => s.value === countyGroup.usStateId) ?? ''}
                                onChange={this.handlers.select.bind(this)}
                                error="A state selection is required."
                            />

                            <ValidatedSelect
                                id="counties"
                                name="counties"
                                label="Counties"
                                component={AsyncSelect}
                                isMulti
                                required
                                loadOptions={this.loadOptions}
                                value={countyGroup.counties ?? ''}
                                getOptionLabel={(option) => `${option.name} (${option.usState.name})`}
                                getOptionValue={(option) => option.id}
                                onChange={this.onCountyChange}
                                placeholder="Start typing a county…"
                                noOptionsMessage={() => 'Start typing a county…'}
                                error="At least one county selection is required."
                            />

                            <Collapse isOpen={countyGroup.usCountyGroupTypeId === 2 || countyGroup.usCountyGroupTypeId === 3}>
                                {tenantSettings.enableCustomizedHoliday && !newCountyGroup && (
                                    <>
                                        <FormCheckbox
                                            id="defineCustomHolidays"
                                            labelText="Define Custom Holidays"
                                            checked={this.state.countyGroup.hasCustomHolidayList}
                                            onChange={this.onDefineCustomHolidaysChanged}
                                            small
                                        />
                                        {/* holiday picker & button – only visible when box is ticked */}
                                        {countyGroup.hasCustomHolidayList && (
                                            <ValidatedSelect
                                                id="countyGroupHoliday"
                                                name="countyGroupHoliday"
                                                label="Define County Group Holidays"
                                                formGroupClassName="mt-2"
                                                isMulti
                                                options={this.state.holidays}
                                                value={this.state.selectedHolidays}
                                                onChange={(val) =>
                                                    this.setState({ selectedHolidays: val || [] })
                                                }
                                                helperText={this.state.selectedHolidays.length === 0 && (
                                                    'No holidays selected - this county group will be treated as observing none.'
                                                )}
                                                showAddButton
                                                onAddButtonClick={() => this.onAddHoliday()}
                                            />
                                        )}
                                    </>
                                )}
                                <FormGroup>
                                    <FormCheckbox
                                        id="countyGroup.breakOutFringeByCounty"
                                        name="countyGroup.breakOutFringeByCounty"
                                        labelText="Break Out Fringe Report By County"
                                        checked={countyGroup.breakOutFringeByCounty || false}
                                        onChange={this.handlers.change}
                                        small
                                    />
                                </FormGroup>
                            </Collapse>
                        </FormGroupColumn>
                    </GroupedRow>

                    <Collapse isOpen={(countyGroup.usCountyGroupTypeId === 2 || countyGroup.usCountyGroupTypeId === 3) && countyGroup.contractUnionWorkTypes?.length > 0}>
                        <SubHeading>Pay Rates</SubHeading>
                        {countyGroup.usCountyGroupTypeId === 2 && (
                            <>
                                <FormCheckbox
                                    id="countyGroup.hasCustomShopRate"
                                    name="countyGroup.hasCustomShopRate"
                                    checked={countyGroup.hasCustomShopRate || false}
                                    labelText="This local has its own pay rate for shop work"
                                    onChange={this.handlers.change}
                                />
                                <Collapse isOpen={Boolean(countyGroup.hasCustomShopRate)}>
                                    <NumericInput
                                        label="Shop Rate"
                                        id="countyGroup.shopRate"
                                        name="countyGroup.shopRate"
                                        value={
                                            countyGroup.shopRate
                                            ?? ''
                                        }
                                        onChange={this.handlers.change}
                                        required={countyGroup.hasCustomShopRate}
                                    />
                                </Collapse>
                            </>
                        )}
                        <ValidatedSelect
                            id="usCountyGroupPaySourceId"
                            name="countyGroup.usCountyGroupPaySourceId"
                            label="How should employees from this local be paid?"
                            formGroupClassName="mt-2"
                            required
                            options={groupPaySources}
                            value={
                                (groupPaySources ?? []).find(
                                    (s) => s.value
                                        === countyGroup.usCountyGroupPaySourceId,
                                ) ?? ''
                            }
                            onChange={this.handlers.select.bind(this)}
                            error="A Pay Source selection is required"
                        />
                        <GroupedRow>
                            <FormGroupColumn third>
                                <Input
                                    label="ADP REG Earning Code"
                                    id="adpRegEarningCode"
                                    name="countyGroup.adpRegEarningCode"
                                    value={countyGroup.adpRegEarningCode ?? ''}
                                    onChange={this.handlers.change}
                                    placeholder="Enter ADP REG Earning Code"
                                />
                            </FormGroupColumn>
                            <FormGroupColumn third>
                                <Input
                                    label="ADP OT Earning Code"
                                    id="adpOtEarningCode"
                                    name="countyGroup.adpOtEarningCode"
                                    value={countyGroup.adpOtEarningCode ?? ''}
                                    onChange={this.handlers.change}
                                    placeholder="Enter ADP OT Earning Code"
                                />
                            </FormGroupColumn>
                            <FormGroupColumn third>
                                <Input
                                    label="ADP DT Earning Code"
                                    id="adpDtEarningCode"
                                    name="countyGroup.adpDtEarningCode"
                                    value={countyGroup.adpDtEarningCode ?? ''}
                                    onChange={this.handlers.change}
                                    placeholder="Enter ADP DT Earning Code"
                                />
                            </FormGroupColumn>
                        </GroupedRow>

                        {(countyGroup.contractUnionWorkTypes || []).length > 0 && (
                            <>
                                <USCountyGroupPayRateGrid
                                    workTypePayRates={countyGroup.workTypePayRates}
                                    contractUnionWorkTypes={countyGroup.contractUnionWorkTypes}
                                    userGroups={userGroups}
                                    onPayRateChange={this.handlePayRateChange}
                                    onSaturdayOTChange={this.handleSaturdayOTChange}
                                    onAddWorkTypePayRate={this.handleAddWorkTypePayRate}
                                    onRemoveWorkTypePayRate={this.handleRemoveWorkTypePayRate}
                                />

                                <USCountyGroupStatePayRateGrid
                                    usStates={usStates}
                                    payRates={countyGroup.statePayRates}
                                    onPayRatesChange={(statePayRates) => {
                                        countyGroup.statePayRates = statePayRates;

                                        this.setState({ countyGroup });
                                    }}
                                />
                            </>
                        )}

                        <SubHeading>Pay Schedule</SubHeading>
                        <GroupedRow>
                            <FormGroup>
                                <FormCheckbox
                                    id="countyGroup.hasUnionPayrollSchedule"
                                    name="countyGroup.hasUnionPayrollSchedule"
                                    checked={countyGroup.hasUnionPayrollSchedule || false}
                                    labelText="This local has its own OT schedule that overrides contracts"
                                    onChange={this.handlers.change}
                                />
                            </FormGroup>

                            <Collapse isOpen={countyGroup.hasUnionPayrollSchedule}>
                                <Block>
                                    <ValidatedSelect
                                        id="contractScheduleTypeId"
                                        name="countyGroup.contractScheduleTypeId"
                                        label="Schedule"
                                        options={contractScheduleTypes}
                                        required
                                        error="A pay schedule must be selected"
                                        value={(
                                            contractScheduleTypes ?? []
                                        ).find(
                                            (x) => countyGroup.contractScheduleTypeId
                                                === x.value,
                                        ) ?? ''}
                                        onChange={
                                            this.handlers.select
                                        }
                                    />
                                </Block>
                            </Collapse>
                        </GroupedRow>

                        {countyGroup.usCountyGroupTypeId === 2 && (
                            <>
                                <FormGroup>
                                    <SubHeading>Shift Differential</SubHeading>
                                    <FormLabel
                                        htmlFor="hasCustomShiftDifferential"
                                        label="local has its own shift differential hours?"
                                        required
                                    />

                                    <TipAlert
                                        containerClassName="mt-1 mb-1"
                                        label={(
                                            <>
                                                Enabling this option and leaving these fields blank will disable shift differential calculations for this local.

                                                {Boolean(regularShiftDifferentialEnabled) && (
                                                    <>
                                                        {' '}
                                                        Your organization&apos;s default shift differential hours are
                                                        {' '}

                                                        <strong>{getDate(tenantSettings.regularShiftDifferentialStart, 'h:mm a')}</strong>
                                                        {' to '}
                                                        <strong>{getDate(tenantSettings.regularShiftDifferentialEnd, 'h:mm a')}</strong>
                                                        .
                                                    </>
                                                )}
                                            </>
                                        )}
                                    />

                                    <YesNoButtons
                                        name="hasCustomShiftDifferential"
                                        value={countyGroup.hasCustomShiftDifferential}
                                        onClick={(value) => {
                                            const { countyGroup } = this.state;
                                            countyGroup.hasCustomShiftDifferential = value;

                                            this.setState({
                                                countyGroup,
                                            });
                                        }}
                                    />

                                    <Collapse isOpen={countyGroup.hasCustomShiftDifferential}>
                                        <Block className="mt-3">
                                            <Row>
                                                <Col>
                                                    <div
                                                        className="ml-4"
                                                        style={{
                                                            width: '100px',
                                                        }}
                                                    >
                                                        <FormLabel
                                                            htmlFor="customShiftDifferentialStart"
                                                            label="Shift starts at"
                                                        />

                                                        <TimePicker
                                                            id="customShiftDifferentialStart"
                                                            name="customShiftDifferentialStart"
                                                            increment={0.25}
                                                            value={customShiftDifferentialStart}
                                                            onChange={this.onTimeChanged}
                                                        />
                                                    </div>
                                                </Col>
                                                <Col>
                                                    <div
                                                        className="ml-1"
                                                        style={{
                                                            width: '100px',
                                                        }}
                                                    >
                                                        <FormLabel
                                                            htmlFor="customShiftDifferentialEnd"
                                                            label="Shift ends at"
                                                        />

                                                        <TimePicker
                                                            id="customShiftDifferentialEnd"
                                                            name="customShiftDifferentialEnd"
                                                            increment={0.25}
                                                            value={customShiftDifferentialEnd}
                                                            onChange={this.onTimeChanged}
                                                        />
                                                    </div>
                                                </Col>
                                                <Col>
                                                    <div
                                                        className="ml-1"
                                                        style={{
                                                            width: '200px',
                                                        }}
                                                    >
                                                        <NumericInput
                                                            id="countyGroup.customShiftDifferentialPayAdjustment"
                                                            label="Shift Differential Pay Adjustment"
                                                            name="countyGroup.customShiftDifferentialPayAdjustment"
                                                            value={
                                                                countyGroup.customShiftDifferentialPayAdjustment
                                                                ?? ''
                                                            }
                                                            startAddon={(
                                                                <span className="text-muted">$</span>
                                                            )}
                                                            onChange={this.handlers.change}
                                                            placeholder="Enter an amount"
                                                            step={0.01}
                                                            min={0}
                                                        />
                                                    </div>
                                                </Col>

                                                <Col />
                                                <Col />
                                                <Col />
                                                <Col />
                                                <Col />
                                            </Row>
                                        </Block>
                                    </Collapse>
                                </FormGroup>

                                <SubHeading>Pay Bump Rules</SubHeading>
                                <GroupedRow>
                                    <FormGroup>
                                        <FormCheckbox
                                            id="countyGroup.hasCustomBumpRules"
                                            name="countyGroup.hasCustomBumpRules"
                                            checked={countyGroup.hasCustomBumpRules || false}
                                            labelText="This local uses bump rules other than standard 2-4-8"
                                            onChange={this.handlers.change}
                                        />
                                    </FormGroup>

                                    <Collapse isOpen={countyGroup.hasCustomBumpRules}>
                                        <TipAlert
                                            label="Leaving this section empty will disable payroll bump rules for this local. To only pay actual hours for a specific local, add the local and specify 0 hours."
                                        />

                                        <USCountyGroupBumpRuleGrid
                                            bumpRules={countyGroup.bumpRules}
                                            usCountyGroups={usCountyGroups}
                                            onBumpRulesChange={(updatedBumpRules) => {
                                                countyGroup.bumpRules = updatedBumpRules;

                                                this.setState({ countyGroup });
                                            }}
                                        />
                                    </Collapse>
                                </GroupedRow>
                            </>
                        )}
                    </Collapse>
                    <FlexCenterRow className="mb-3">
                        <SaveButton
                            saving={saving}
                            name="countyGroupForm"
                            label={newCountyGroup
                                ? 'Save New County Group'
                                : 'Save'}
                        />
                    </FlexCenterRow>
                </AppPageForm>

                <CreateHolidayForm
                    show={showAddHolidayForm}
                    isCompanyHoliday={false}
                    countyGroupId={this.getCountyGroupId()}
                    onHolidayCreated={this.onHolidayCreated}
                    onClose={this.onCloseHolidayForm}
                />
            </>
        );
    }

    render() {
        const { loading } = this.state;

        if (loading) {
            return <Progress />;
        }

        return this.renderForm();
    }
}
export default USCountyGroupForm;
